import React from 'react';
import { EmailBlock, FooterBlockContent } from '../types';
import { Facebook, Twitter, Instagram, Linkedin, Youtube } from 'lucide-react';

interface FooterBlockProps {
  block: EmailBlock;
  isSelected: boolean;
  onSelect: () => void;
  isDarkMode: boolean;
}

const SOCIAL_ICONS = {
  facebook: Facebook,
  twitter: Twitter,
  instagram: Instagram,
  linkedin: Linkedin,
  youtube: Youtube
};

export const FooterBlock: React.FC<FooterBlockProps> = ({
  block,
  isSelected,
  onSelect,
  isDarkMode
}) => {
  const content = block.content as FooterBlockContent;

  const style: React.CSSProperties = {
    backgroundColor: content.backgroundColor || '#f3f4f6',
    padding: content.padding || '30px 20px',
    color: content.textColor || '#6b7280',
    fontSize: content.fontSize || 12,
    ...block.styles
  };

  return (
    <div style={style} className="border-t border-gray-200 dark:border-gray-700">
      <div className="space-y-4">
        {/* Company Info */}
        {content.companyInfo && (
          <div dangerouslySetInnerHTML={{ __html: content.companyInfo }} />
        )}

        {/* Social Links */}
        {content.socialLinks && content.socialLinks.length > 0 && (
          <div className="flex items-center gap-3">
            {content.socialLinks.map((social, index) => {
              const Icon = SOCIAL_ICONS[social.platform];
              if (!Icon) return null;
              return (
                <a
                  key={index}
                  href={social.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`
                    ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}
                  `}
                >
                  <Icon size={20} />
                </a>
              );
            })}
          </div>
        )}

        {/* Subscription Management Links */}
        {(content.showUnsubscribeLink || content.showPreferenceCenter) && (
          <div className="flex flex-wrap gap-4 text-xs">
            {content.showUnsubscribeLink && (
              <a
                href="{{unsubscribeLink}}"
                className={`
                  underline
                  ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}
                `}
              >
                {content.unsubscribeText || 'Unsubscribe'}
              </a>
            )}
            {content.showPreferenceCenter && (
              <a
                href="{{preferenceCenterLink}}"
                className={`
                  underline
                  ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'}
                `}
              >
                {content.preferenceCenterText || 'Manage Preferences'}
              </a>
            )}
          </div>
        )}

        {/* Copyright */}
        {content.copyrightText && (
          <div className="text-xs opacity-75">
            {content.copyrightText}
          </div>
        )}
      </div>
    </div>
  );
};
